import { useState, useEffect, useRef } from 'react';
import { useFrame } from '@react-three/fiber';

import { Text3D } from '../reusable/Text3D/index';
import getCLTOrderLayerModel from './models/getCLTOrderLayerModel';
import getBeamModel from './models/getBeamModel';
import getJDLayerModel from './models/getJDLayerModel';
import getBeamSingleModel from './models/getBeamSingleModel';
import getBodenlagerStackModel from './models/getBodenlagerStackModel';
import HyperBeamModel from './models/HyperBeamModel';
import { CurvedBeamModel, CurvedLayerModel, CurvedMultiLayerModel, StraightMultiLayerModel } from './models/CurvedBeamModel';

import _ from 'lodash';
import GlowingOutlineBox from './models/ErrorIndicators/GlowingOutlineBox';
import { resolveBeamOptions } from './beamRenderConfig';
import { WoodOutlinedColoredBeam } from './models/ModelElements/WoodOutlinedColoredBeam';
import { LamellaTexturedBeam } from './models/ModelElements/LamellaTexturedBeam';
import { InstancedLamellaBatch } from './models/ModelElements/InstancedLamellaBatch';
import { WoodOutlined } from './models/ModelElements/WoodOutlined';
import { Insulation } from './models/ModelElements/Insulation';
import { LamellaBase } from './models/ModelElements/LamellaBase';
import { LamellaReady } from './models/ModelElements/LamellaReady';

const INSTANCEABLE_COMPONENTS = new Set([WoodOutlinedColoredBeam, LamellaTexturedBeam, WoodOutlined, Insulation, LamellaBase, LamellaReady]);
const DEFAULT_INSTANCING_MIN_COUNT = 20;

function isValidTransformArray(value) {
  return Array.isArray(value) && value.length === 3 && value.every((item) => Number.isFinite(Number(item)));
}

function isInstancingCandidate(entry) {
  if (!entry || !INSTANCEABLE_COMPONENTS.has(entry.component)) return false;
  if (!isValidTransformArray(entry.position)) return false;
  if (!isValidTransformArray(entry.scale)) return false;
  return true;
}

function getInstancingVariant(component) {
  if (component === LamellaTexturedBeam) return 'textured';
  if (component === WoodOutlined) return 'outlined';
  if (component === Insulation) return 'insulation';
  if (component === LamellaBase) return 'base';
  if (component === LamellaReady) return 'ready';
  return 'wood';
}

function getInstancingMinCount(options) {
  const parsed = Number(_.get(options, 'lamellaInstancingMinCount', DEFAULT_INSTANCING_MIN_COUNT));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_INSTANCING_MIN_COUNT;
}

function Model3D({ type, modelData, modelRef, customPosition, idx = null, callback = null, isHighlighted = false, setHighlightedIdx = null, isModalOpened = false, beamRenderProfile = 'default' }) {
  const position = customPosition || [0, 0, 0];
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const textRef = useRef();

  useFrame(({ clock }) => {
    if (textRef.current) {
      const t = clock.getElapsedTime();
      const pulse = 1 + 0.08 * Math.sin(t * 2);
      textRef.current.scale.set(pulse, pulse, pulse);
    }
  });
  useEffect(() => {
    if (!isModalOpened) {
      setIsClicked(false);
      setIsHovered(false);
    }
  }, [isModalOpened]);

  const onPointerOver = () => {
    if (!isClicked && setHighlightedIdx) {
      setIsHovered(true);
      setHighlightedIdx(idx);
    }
  };

  const onPointerOut = () => {
    if (!isClicked && setHighlightedIdx) {
      setIsHovered(false);
      setHighlightedIdx(null);
    }
  };

  const onClick = (event) => {
    event.stopPropagation();
    setIsClicked(true);
    if (callback) callback(idx);
  };

  const modelColor = isClicked || isHighlighted ? 'orange' : isHovered ? '#eff543' : '#848484';

  let modelDataArray = [];
  let resolvedBeamOptions = null;
  let customModel = null;
  const sceneGroupTransform = _.get(modelData, 'sceneGroupTransform', null);
  switch (type) {
    case 'cltorder':
      modelDataArray = getCLTOrderLayerModel(modelData.order, modelData.layer, modelData.layerId, modelData.suppressLabels);
      break;
    case 'jdlayer':
      modelDataArray = getJDLayerModel(modelData.order, _.get(modelData, 'sequenceCheck', undefined));
      break;
    case 'beamOrder': {
      resolvedBeamOptions = resolveBeamOptions(_.get(modelData, 'beamOptions', null), _.get(modelData, 'beamRenderProfile', beamRenderProfile));
      modelDataArray = getBeamModel(modelData.order, modelData.layer, modelData.layerId, resolvedBeamOptions);
      break;
    }
    case 'beamSingle': {
      resolvedBeamOptions = resolveBeamOptions(_.get(modelData, 'beamOptions', null), _.get(modelData, 'beamRenderProfile', beamRenderProfile));
      modelDataArray = getBeamSingleModel(modelData.order, modelData.layer, modelData.layerId, resolvedBeamOptions);
      break;
    }
    case 'hyperBeamModel': {
      resolvedBeamOptions = resolveBeamOptions(_.get(modelData, 'beamOptions', null), _.get(modelData, 'beamRenderProfile', beamRenderProfile));
      customModel = <HyperBeamModel hyperBeam={_.get(modelData, 'hyperBeam', null)} beamOptions={resolvedBeamOptions} />;
      break;
    }
    case 'curvedBeamOrder': {
      resolvedBeamOptions = resolveBeamOptions(_.get(modelData, 'beamOptions', null), _.get(modelData, 'beamRenderProfile', beamRenderProfile));
      customModel = <CurvedBeamModel order={modelData.order} beamOptions={resolvedBeamOptions} />;
      break;
    }
    case 'curvedMultiLayerOrder': {
      resolvedBeamOptions = resolveBeamOptions(_.get(modelData, 'beamOptions', null), _.get(modelData, 'beamRenderProfile', beamRenderProfile));
      customModel = <CurvedMultiLayerModel order={modelData.order} beamOptions={resolvedBeamOptions} />;
      break;
    }
    case 'curvedLayerOrder': {
      resolvedBeamOptions = resolveBeamOptions(_.get(modelData, 'beamOptions', null), _.get(modelData, 'beamRenderProfile', beamRenderProfile));
      customModel = <CurvedLayerModel order={modelData.order} layer={modelData.layer} beamOptions={resolvedBeamOptions} />;
      break;
    }
    case 'straightMultiLayerOrder': {
      resolvedBeamOptions = resolveBeamOptions(_.get(modelData, 'beamOptions', null), _.get(modelData, 'beamRenderProfile', beamRenderProfile));
      customModel = <StraightMultiLayerModel order={modelData.order} beamOptions={resolvedBeamOptions} />;
      break;
    }
    case 'bodenlagerStacking':
      modelDataArray = getBodenlagerStackModel(modelData.order, modelData.layer, modelData.layerId, _.get(modelData, 'beamOptions', null));
      break;
    default:
      console.error(`Unknown type: ${type}`);
      break;
  }

  if (customModel) {
    return (
      <group ref={modelRef} position={position} onPointerOver={onPointerOver} onPointerOut={onPointerOut} onClick={onClick}>
        {customModel}
      </group>
    );
  }

  if (!modelDataArray || modelDataArray.length === 0) {
    return <group ref={modelRef} />;
  }

  const stackDimensions = modelDataArray.reduce(
    (dims, data) => {
      const { position, scale } = data;
      if (position && scale) {
        dims.width = Math.max(dims.width, scale[0]);
        dims.height = Math.max(dims.height, scale[1]);
        dims.length += scale[2];
      }
      return dims;
    },
    { width: 0, height: 0, length: 0 }
  );

  const boxWidth = stackDimensions.width * 1.1;
  const boxHeight = stackDimensions.height;
  const boxLength = 20;
  const showHighlightBox = true;

  const needsPolyOffset = typeof type === 'string' && type.startsWith('beam');
  const polygonMaterialExtras = needsPolyOffset
    ? {
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1
      }
    : {};

  const isLayerModel = type === 'jdlayer' || type === 'cltorder';
  const shouldTryInstancing = isLayerModel || ((type === 'beamOrder' || type === 'beamSingle') && _.get(resolvedBeamOptions, 'useLamellaInstancing', false));
  const minInstancingCount = isLayerModel ? 2 : getInstancingMinCount(resolvedBeamOptions);
  let regularRenderEntries = modelDataArray;
  let instancedBatchEntries = [];

  if (shouldTryInstancing) {
    const candidateEntries = [];
    const passthroughEntries = [];

    modelDataArray.forEach((entry) => {
      if (isInstancingCandidate(entry)) {
        candidateEntries.push(entry);
      } else {
        passthroughEntries.push(entry);
      }
    });

    if (candidateEntries.length >= minInstancingCount) {
      const batchesByKey = new Map();
      candidateEntries.forEach((entry, index) => {
        const variant = getInstancingVariant(entry.component);
        const transparent = !!entry.transparent;
        const opacity = Number.isFinite(Number(entry.opacity)) ? Number(entry.opacity) : 1;
        const batchKey = [
          variant,
          transparent ? 1 : 0,
          opacity,
          polygonMaterialExtras.polygonOffset ? 1 : 0,
          polygonMaterialExtras.polygonOffsetFactor ?? 1,
          polygonMaterialExtras.polygonOffsetUnits ?? 1
        ].join(':');

        if (!batchesByKey.has(batchKey)) {
          batchesByKey.set(batchKey, {
            key: `instanced-${batchKey}`,
            variant,
            transparent,
            opacity,
            polygonOffset: polygonMaterialExtras.polygonOffset,
            polygonOffsetFactor: polygonMaterialExtras.polygonOffsetFactor,
            polygonOffsetUnits: polygonMaterialExtras.polygonOffsetUnits,
            instances: []
          });
        }

        batchesByKey.get(batchKey).instances.push({
          position: entry.position,
          scale: entry.scale,
          rotation: entry.rotation,
          color: entry.color,
          key: entry.key || `instance-${index}`
        });
      });

      instancedBatchEntries = Array.from(batchesByKey.values());
      regularRenderEntries = passthroughEntries;
    }
  }

  return (
    <group ref={modelRef} position={position} onPointerOver={onPointerOver} onPointerOut={onPointerOut} onClick={onClick}>
      {showHighlightBox && <GlowingOutlineBox size={[boxWidth * 4, boxHeight, boxLength]} highlighted={isHovered || isHighlighted} />}

      {/* Stack index number */}
      {idx != null && (
        <Text3D
          ref={textRef}
          position={[0, boxHeight + 2, -10]}
          fontSize={0.8}
          fontWeight={400}
          color={isHovered || isHighlighted ? 'yellow' : '#888888'} // dim when inactive
          outlineWidth={isHovered || isHighlighted ? 0.02 : 0}
          outlineColor={isHovered || isHighlighted ? 'white' : '#000000'}
          anchorX="center"
          anchorY="middle">
          {idx + 1}
        </Text3D>
      )}

      <group position={sceneGroupTransform?.position || [0, 0, 0]} rotation={sceneGroupTransform?.rotation || [0, 0, 0]}>
        {instancedBatchEntries.map((batch) => (
          <InstancedLamellaBatch
            key={batch.key}
            variant={batch.variant}
            instances={batch.instances}
            polygonOffset={batch.polygonOffset}
            polygonOffsetFactor={batch.polygonOffsetFactor}
            polygonOffsetUnits={batch.polygonOffsetUnits}
            transparent={batch.transparent}
            opacity={batch.opacity}
          />
        ))}

        {regularRenderEntries.map(({ component: Component, position, scale, rotation, key: itemKey, ...rest }, index) => {
          const renderKey = itemKey || `${index}-${(position || []).join(',')}-${(scale || []).join(',')}-${(rotation || []).join(',')}`;
          return (
            <Component
              {...rest}
              position={position}
              scale={scale}
              rotation={rotation}
              key={renderKey}
              polygonOffset={polygonMaterialExtras.polygonOffset}
              polygonOffsetFactor={polygonMaterialExtras.polygonOffsetFactor}
              polygonOffsetUnits={polygonMaterialExtras.polygonOffsetUnits}
              material={{
                color: modelColor,
                emissive: isHovered ? 'yellow' : 'black',
                ...polygonMaterialExtras
              }}
            />
          );
        })}
      </group>
    </group>
  );
}

export default Model3D;
